import PageLayout from 'components/pages/PageLayout'
import { useEffect } from 'react'
import PageMeta from 'components/PageMeta'
import { useRouter } from 'next/router'
import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import LoadingPage from 'components/pages/LoadingPage'
import ErrorPage from 'components/pages/ErrorPage'
import NotFoundPage from 'components/pages/NotFoundPage'
import { useShiftPageQuery } from 'components/pages/ShiftPage/types/ShiftPageQuery'
import ShiftMenu from 'components/pages/ShiftPage/ShiftMenu'
import TruckInformationSection from 'components/HeroSection'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import TruckSectionContent from 'components/pages/ShiftPage/TruckSectionContent'
import AppAds from 'components/AppAds'
import useReferrals from 'hooks/useReferrals'
import pathFromUrl from 'utils/pathFromUrl'
import LinksSection from 'components/HeroSection/LinksSection'
import stripTimeZone from 'utils/stripTimeZone'

type Props = Record<string, never>

type Params = {
  locationId: string
}

const ShiftPage = () => {
  const router = useRouter()
  const { locationId: slug } = router.query as Params

  const locationIdParam = slug.split('-')[0]
  const { data, loading, error } = useShiftPageQuery({
    variables: {
      id: parseInt(locationIdParam, 10)
    },
    fetchPolicy: 'cache-and-network'
  })

  const location = data?.location

  useReferrals()
  // if location is not active redirect to lot
  useEffect(() => {
    if (location && !location.active) {
      window.location.href = location.lot.lotUrl!
    }
  }, [location, router])

  if (loading && !location) return <LoadingPage />
  const notFoundError =
    error &&
    error.message.includes(
      'Cannot return null for non-nullable field Query.location'
    )
  if (error && !notFoundError) return <ErrorPage message={error.message} />
  if (
    !location ||
    notFoundError ||
    !location.active ||
    !location.truck.active ||
    !location.truck.restaurant.active
  ) {
    return <NotFoundPage />
  }

  const heroLinks = {
    twitter: location.truck.twitter,
    facebook: location.truck.facebook,
    instagram: location.truck.instagram,
    website: location.truck.website
  }
  return (
    <PageLayout highlightCartItems={false} fullWidth staticPage>
      <PageMeta
        canonicalPath={pathFromUrl(location.lot.lotUrl)}
        coordinates={[location.lot.latitude, location.lot.longitude]}
        description={`Order from ${location.truck.name} for a shift on ${format(
          parseISO(stripTimeZone(location.startTime)),
          'M/d/y'
        )} at ${location.lot.name}, ${location.lot.fullAddress}`}
        image={location.foodImage.medium}
        ldJson={data?.structuredData?.shiftPage}
        path={`/shifts/${slug}`}
        title={`Best Food Trucks | ${location.truck.name} shift on ${format(
          parseISO(stripTimeZone(location.startTime)),
          'M/d/y'
        )}`}
      />

      <div className="w-full">
        <TruckInformationSection
          content={
            <TruckSectionContent
              instantBook={location.truck.instantBookEnabled}
              location={location}
              preferredVendor={location.truck.preferredVendor}
              preferredVendorBadge={location.truck.preferredVendorBadge}
              ratingInfo={location.truck.ratingInfo}
              scheduleUrl={location.truck.scheduleUrl}
              socialLinks={heroLinks}
              truckName={location.truck.name}
            />
          }
          imageSrc={location.truck.image.original!}
          links={<LinksSection socialLinks={heroLinks} />}
          size="small"
        />
        <div>
          <ShiftMenu location={location} hideLocationInformation />
        </div>
        <div>
          <AppAds />
        </div>
      </div>
    </PageLayout>
  )
}

const ShiftPageWithApollo = withApollo({ ssr: false })(ShiftPage as NextPage)
export default ShiftPageWithApollo

// eslint-disable-next-line react/only-export-components
export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

// eslint-disable-next-line react/only-export-components
export const getStaticProps = getStaticApolloProps<Props, Params>(ShiftPage, {
  revalidate: 60 // 1 minute
})
